import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Card,
  cn,
  OrderLineItem,
  SimpleSelect,
  Skeleton,
} from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { ExchangeQuery } from '~/api';
import { CorsoAIBadgeTooltip } from '~/components/ai/AIFeatures';
import ProductImage from '~/components/ProductImage';
import { useStoreId } from '~/providers/StoreUserProvider';
import { formatter } from 'corso-web-lib';

// ! MUCH OF THIS DUPLICATED FROM `exchange-options.ts` and `resolution-component.tsx` in `crew-customer-ui`

const productNotFoundImgUrl =
  'https://cdn.corso.com/img/image-not-available.jpeg';

type Product = CrewMerchantUi.VariantExchangeResponse['products'][number];
type ExchangeRecommendation =
  CrewMerchantUi.VariantExchangeResponse['exchangeRecommendation'];
type Variant = Product['variants'][number];

type ProductOption = Omit<NonNullable<Product['options'][number]>, 'values'> & {
  values: VariantOption[];
};

type VariantOption = {
  label: string;
  value: string;
  group: ProductOption['name'];
  available: boolean;
};

type State = {
  selectedProduct: Product;
  productOptions: ProductOption[];
  selectedVariant: Variant | undefined;
  selectedOptions: Record<ProductOption['name'], VariantOption>;
};

const createVariantOption = (group: string) => (value: string) =>
  ({
    value,
    label: value,
    group,
    available: true,
  }) satisfies VariantOption;

const createProductOption = (
  option: NonNullable<Product['options']>[number],
): ProductOption => ({
  ...option,
  values: option.values.map(createVariantOption(option.name)),
});

const formatVariantExchangeResponse = (
  res: CrewMerchantUi.VariantExchangeResponse['products'],
): Product[] =>
  res.map(({ options, ...rest }) => ({
    ...rest,
    options: [...options].sort((a, b) => a.position - b.position),
  }));

const matchVariantByOption =
  ({ group, value }: VariantOption) =>
  (v: Variant) => {
    const option = v.options.find((o) => o.name === group);

    return option ? option.value === value : false;
  };

const checkAvailability = (variants: Variant[]) => (option: VariantOption) => ({
  ...option,
  available: variants.some(matchVariantByOption(option)),
});

const updateOptionAvailability =
  (variants: Variant[], selected: VariantOption) =>
  ({ values, ...option }: ProductOption) =>
    ({
      ...option,
      values:
        // don't update the availability of the option group that changed e.g. if color:black is selected, don't update color options
        option.name === selected.group
          ? values
          : // e.g if color:black is selected, only show the sizes for black as available
            values.map(
              checkAvailability(
                // only need to check the variants that match the selected option e.g. if color:black is selected, only check the black variants
                variants.filter(matchVariantByOption(selected)),
              ),
            ),
    }) satisfies ProductOption;

const initializeSelectedOptions = (
  productOptions: ProductOption[],
  selectedVariantOptions: Variant['options'],
) =>
  Object.fromEntries(
    selectedVariantOptions.map(({ name: optionName, value }) => {
      const productOption = productOptions.find(
        ({ name }) => name === optionName,
      );

      const selectedOption = productOption?.values.find(
        (option) => option.value === value,
      );

      if (selectedOption === undefined) {
        throw new Error('Selected option not found');
      }

      return [optionName, selectedOption];
    }),
  ) satisfies State['selectedOptions'];

const simulateChangeVariantOption = (
  productOptions: ProductOption[],
  selectedOptions: State['selectedOptions'],
) => {
  const firstProductOption = productOptions[0];
  if (firstProductOption === undefined) {
    throw new Error('No options for selected product');
  }
  const simulatedChangedVariantOption =
    selectedOptions[firstProductOption.name];
  if (simulatedChangedVariantOption === undefined) {
    throw new Error('No options for selected product');
  }

  return simulatedChangedVariantOption;
};

const changeProduct = (selectedProduct: Product): State => {
  const { variants, options } = selectedProduct;
  const productOptions = options.map(createProductOption);
  const firstVariant = variants[0];

  if (firstVariant === undefined) {
    // again, this shouldn't happen because the products should have variants
    throw new Error('No variants for selected product');
  }

  const selectedOptions = initializeSelectedOptions(
    productOptions,
    firstVariant.options,
  );

  return {
    selectedProduct,
    selectedVariant: firstVariant,
    selectedOptions,
    productOptions: productOptions.map(
      updateOptionAvailability(
        variants,
        simulateChangeVariantOption(productOptions, selectedOptions),
      ),
    ),
  };
};

const getSelectVariant = (
  variants: Variant[],
  selectedOptions: VariantOption[],
) =>
  variants.find((variant) =>
    selectedOptions.every((option) => matchVariantByOption(option)(variant)),
  );

const changeVariantOption = (
  state: State,
  selectedOption: VariantOption,
): State => {
  const { variants } = state.selectedProduct;

  const updatedSelectedOptions = {
    ...state.selectedOptions,
    [selectedOption.group]: selectedOption,
  };

  return {
    ...state,
    selectedOptions: updatedSelectedOptions,
    selectedVariant: getSelectVariant(
      variants,
      Object.values(updatedSelectedOptions),
    ),
    productOptions: state.productOptions.map(
      updateOptionAvailability(variants, selectedOption),
    ),
  };
};
const useExchangeProductData = (
  products: Product[],
  initialProductId: string,
) => {
  const initialSate = useMemo(() => {
    const initialProduct =
      products.find((p) => p.idFromPlatform === initialProductId) ??
      products[0];

    if (initialProduct === undefined) {
      throw new Error('No products in exchange data');
    }

    return changeProduct(initialProduct);
  }, [products, initialProductId]);

  const [state, setState] = useState<State>(initialSate);

  const onProductChange = useCallback(
    (productId: Product['idFromPlatform']) => {
      const product = products.find(
        ({ idFromPlatform }) => idFromPlatform === productId,
      );
      if (product === undefined) {
        throw new Error('No product found');
      }

      setState(changeProduct(product));
    },
    [products],
  );

  const onVariantOptionChange = useCallback(
    (optionName: ProductOption['name']) =>
      (selectedValue: VariantOption['value']) => {
        /**
         * This is a workaround to prevent the error that occurs when the
         * select value is empty that happens when the product select changes
         * and the new initial selected product options is not same as the previous
         * product's initial selected options.
         */
        if (!selectedValue) return;

        const productOption = state.productOptions.find(
          ({ name }) => name === optionName,
        );
        const variantOption = productOption?.values.find(
          ({ value }) => value === selectedValue,
        );

        if (variantOption === undefined) {
          throw new Error('No variant option found');
        }

        setState((previousState) =>
          changeVariantOption(previousState, variantOption),
        );
      },
    [state.productOptions],
  );

  return {
    ...state,
    products,
    onProductChange,
    onVariantOptionChange,
  };
};

function useVariantExchangeOptions(idFromPlatform: string) {
  const storeId = useStoreId();
  return useQuery({
    queryKey: ['variantExchangeOptions', storeId, idFromPlatform],
    queryFn: () =>
      api
        .store(storeId)
        .variantExchangeOptions(idFromPlatform, {})
        .then((r) => r.products),
    select: formatVariantExchangeResponse,
  });
}

function useRecommendProduct(idFromPlatform: string, query: ExchangeQuery) {
  const storeId = useStoreId();
  return useQuery({
    queryKey: ['variantExchangeOptions', storeId, idFromPlatform, query],
    enabled: Object.values(query).length > 0,
    queryFn: () =>
      api
        .store(storeId)
        .variantExchangeOptions(idFromPlatform, query)
        .then((r) => r.exchangeRecommendation),
  });
}

function VariantSelectOption({ option }: { option: VariantOption }) {
  return (
    <div className={cn('flex flex-wrap items-baseline gap-2')}>
      <span className={cn(!option.available && 'line-through')}>
        {option.label}
      </span>
      {!option.available && <Badge>Unavailable</Badge>}
    </div>
  );
}

function ProductSelectOption({ option }: { option: Product }) {
  return (
    <div className="flex w-full items-center gap-2">
      <img
        src={option.imageUrl ?? productNotFoundImgUrl}
        aria-hidden
        className="h-6 w-6 shrink-0 rounded-full"
        alt="Image for product option"
      />
      <span>{option.name}</span>
    </div>
  );
}

function VariantExchange({
  exchangeProducts,
  initialProductId,
  onChange,
}: {
  exchangeProducts: Product[];
  initialProductId: string;
  onChange: (selectedVariant: Variant) => void;
}) {
  const { onProductChange, onVariantOptionChange, ...state } =
    useExchangeProductData(exchangeProducts, initialProductId);

  useEffect(() => {
    if (!state.selectedVariant) return;
    onChange(state.selectedVariant);
  }, [state.selectedVariant, onChange]);

  const imgSrc =
    state.selectedVariant?.imgUrl ?? state.selectedProduct.imageUrl;

  return (
    <div className="flex flex-col items-center gap-4 md:flex-row">
      <ProductImage
        src={imgSrc}
        alt={state.selectedVariant?.titleWithOptions ?? ''}
        className="size-32 rounded-lg"
      />
      <div className="grow space-y-2">
        <SimpleSelect
          label="Product"
          options={state.products.map((option) => ({
            label: <ProductSelectOption option={option} />,
            value: option.idFromPlatform,
          }))}
          value={state.selectedProduct.idFromPlatform}
          onChange={onProductChange}
        />

        {!state.selectedProduct.hasOnlyDefaultVariant &&
          state.productOptions.map(({ name, values }) => (
            <SimpleSelect
              key={name}
              label={name}
              options={values.map((option) => ({
                label: <VariantSelectOption option={option} />,
                value: option.value,
              }))}
              value={state.selectedOptions[name]?.value} // although nullish, it really should always exist, if not, this will change from controlled/uncontrolled or vice versa
              onChange={onVariantOptionChange(name)}
            />
          ))}

        {state.selectedVariant?.quantity !== undefined && (
          <p
            className={cn(
              'text-xs text-neutral-600',
              state.selectedVariant.quantity <= 0 && 'text-red-700',
            )}
          >
            {formatter.count(state.selectedVariant.quantity)} available
          </p>
        )}
      </div>
    </div>
  );
}

function RecommendExchangeItem({
  recommendation,
}: {
  recommendation: ExchangeRecommendation;
}) {
  if (!recommendation) return null;

  type RecommendationType = NonNullable<ExchangeRecommendation>['method'];

  const recommendationMessage = {
    sameVariant:
      'Same item recommended - customer reported receiving a wrong or damaged product.',
    historicalExchange:
      'Matched to the most common exchange destination for this exact product and return reason.',
    aiVariantSwap:
      "Recommended a different variant of the same product to address the customer's fit issue.",
    aiPopular:
      'Selected from the most popular exchanges across the store, matched to a similar price range.',
    aiProductType:
      'Best match found within the same product type and price range, based on what similar customers have exchanged for.',
  } satisfies Record<RecommendationType, string>;

  return (
    <div className="relative overflow-hidden rounded-lg">
      <div className="absolute inset-x-0 top-0 h-1 ai-shimmer" />
      <Card>
        <Card.Line>
          <Card.Heading>Recommended Exchange</Card.Heading>
          <CorsoAIBadgeTooltip
            message={recommendationMessage[recommendation.method]}
          />
        </Card.Line>

        {recommendation.customerRecommendationMessage && (
          <p className="text-sm text-neutral-500">
            {recommendation.customerRecommendationMessage}
          </p>
        )}

        <OrderLineItem
          title={recommendation.product.name}
          src={recommendation.variant.imgUrl ?? recommendation.product.imageUrl}
          options={recommendation.variant.options}
        />
      </Card>
    </div>
  );
}

export function ClaimCreateVariantExchange({
  idFromPlatform,
  selectedQuantity,
  reasonId,
  reasonDetailId,
  variantIdFromPlatform,
  onChange,
}: {
  /** `product.idFromPlatform` to query available exchange options. */
  idFromPlatform: string;
  variantIdFromPlatform: string | null;
  /** The quantity of the line item being exchanged. */
  selectedQuantity: number;
  reasonId: number;
  reasonDetailId?: number;
  onChange: (
    variant: NonNullable<
      CrewMerchantUi.ClaimCreate['claimLineItems'][number]['variantExchangeLineItem']
    >,
  ) => void;
}) {
  const { isLoading, isError, error, data } =
    useVariantExchangeOptions(idFromPlatform);

  const { data: exchangeRecommendation, isLoading: isRecommendationLoading } =
    useRecommendProduct(
      idFromPlatform,
      reasonDetailId && variantIdFromPlatform
        ? {
            variantIdFromPlatform,
            reasonDetailId,
            isExchangeOfExchange: false,
            reasonId,
          }
        : {},
    );

  const callback = useCallback(
    (selectedVariant: Variant) =>
      onChange({
        idFromPlatform: selectedVariant.idFromPlatform,
        quantity: selectedQuantity,
        price: selectedVariant.price,
      }),
    [onChange, selectedQuantity],
  );

  // TODO improve loading/error/data (zero, one, many) states
  if (isError) return <Alert variant="critical" heading={error.message} />; // TODO review error messages
  if (isLoading || !data)
    return <Skeleton.Rectangle height="2rem" width="100%" />;

  if (!data.length) {
    return (
      <Alert heading="No Product Options Found. Select another resolution." />
    );
  }

  return (
    <>
      {isRecommendationLoading ? (
        <Skeleton.Rectangle height="2rem" width="100%" />
      ) : (
        <RecommendExchangeItem recommendation={exchangeRecommendation} />
      )}
      <VariantExchange
        exchangeProducts={data}
        onChange={callback}
        initialProductId={idFromPlatform}
      />
    </>
  );
}
